import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { workspaceApi } from '../../api/workspaceApi';
import { showErrorToast, showSuccessToast } from '../../lib/toast';

const originsFromText = (text) => text.split('\n').map(line => line.trim()).filter(Boolean);

const PublishChatModal = ({ workspaceId, open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [embed, setEmbed] = useState(null);
  const [originsText, setOriginsText] = useState('');
  const [failed, setFailed] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  useEffect(() => {
    if (!open || !workspaceId) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setEmbed(null);
    setConfirmRevoke(false);
    workspaceApi.getEmbed(workspaceId)
      .then(data => {
        if (cancelled) return;
        setEmbed(data);
        setOriginsText((data.allowed_origins || []).join('\n'));
      })
      .catch(err => {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setEmbed(null);
          setOriginsText('');
          return;
        }
        console.error('Failed to load chat embed:', err);
        setFailed(true);
        showErrorToast('Could not load this chat.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, workspaceId]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const data = await workspaceApi.updateEmbed(workspaceId, patch);
      setEmbed(data);
      setOriginsText((data.allowed_origins || []).join('\n'));
      showSuccessToast('Widget updated.');
    } catch (err) {
      console.error('Failed to update chat embed:', err);
      showErrorToast('Could not update this widget.');
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setSaving(true);
    try {
      const data = await workspaceApi.publishEmbed(workspaceId, {
        allowed_origins: originsFromText(originsText),
      });
      setEmbed(data);
      setOriginsText((data.allowed_origins || []).join('\n'));
      showSuccessToast('Chat published.');
    } catch (err) {
      console.error('Failed to publish chat embed:', err);
      showErrorToast('Could not publish this chat.');
    } finally {
      setSaving(false);
    }
  };

  const saveOrigins = () => {
    save({ allowed_origins: originsFromText(originsText), enabled: embed?.enabled !== false });
  };

  const revoke = async () => {
    setSaving(true);
    try {
      await workspaceApi.revokeEmbed(workspaceId);
      setEmbed(null);
      setOriginsText('');
      setConfirmRevoke(false);
      onClose();
      showSuccessToast('Widget revoked.');
    } catch (err) {
      console.error('Failed to revoke chat embed:', err);
      showErrorToast('Could not revoke this widget.');
      setSaving(false);
    }
  };

  const copySnippet = async () => {
    if (!embed?.snippet) return;
    try {
      await navigator.clipboard.writeText(embed.snippet);
      showSuccessToast('Snippet copied.');
    } catch (err) {
      console.error('Failed to copy snippet:', err);
      showErrorToast('Could not copy the snippet.');
    }
  };

  if (!open) return null;

  const originsField = (
    <>
      <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-gray-500">
        Allowed websites
        <textarea
          value={originsText}
          onChange={event => setOriginsText(event.target.value)}
          rows={4}
          placeholder={'https://example.com\nhttps://www.example.com'}
          className="mt-1 w-full rounded-xl border border-gray-200 p-3 font-mono text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
        />
      </label>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        One origin per line, including https. The widget only runs on the sites you list. The host page has to send a referrer so the widget can tell which site it is on.
      </p>
    </>
  );

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-gray-900/40"
        aria-label="Close publish dialog"
        onClick={onClose}
      />
      {failed && !embed ? (
        <div className="relative rounded-3xl bg-white px-6 py-5 text-sm text-gray-600 shadow-2xl dark:bg-gray-950 dark:text-gray-300">
          Could not load this chat.
          <button type="button" onClick={onClose} className="ml-3 font-semibold text-emerald-700">
            Close
          </button>
        </div>
      ) : loading && !embed ? (
        <div className="relative rounded-3xl bg-white px-6 py-5 text-sm text-gray-600 shadow-2xl dark:bg-gray-950 dark:text-gray-300">
          Loading…
        </div>
      ) : (
        <div className="relative w-full max-w-lg rounded-3xl border border-gray-200 bg-white p-5 shadow-2xl dark:border-gray-800 dark:bg-gray-950">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Publish chat</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Paste a snippet on another site. It adds a chat button in the corner. Visitors can open it without a Manteo login. Each question counts against your monthly message limit.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
          {embed ? (
            <>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Snippet
                <textarea
                  readOnly
                  value={embed.snippet}
                  rows={11}
                  className="mt-1 w-full resize-none rounded-xl border border-gray-200 bg-gray-50 p-3 font-mono text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                />
              </label>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Open Preview to try each layout and copy install steps. You can still set <span className="font-mono">data-color</span>, <span className="font-mono">data-message</span>, and <span className="font-mono">data-question</span> on the script tag.
              </p>
              <div className="mt-2 flex justify-end gap-2">
                <a
                  href={`/embed?key=${encodeURIComponent(embed.public_key)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  Preview
                </a>
                <button
                  type="button"
                  onClick={copySnippet}
                  className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                >
                  Copy snippet
                </button>
              </div>
              {originsField}
              <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => save({ enabled: !embed.enabled })}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900 disabled:opacity-50 dark:text-gray-300"
                >
                  {embed.enabled ? 'Pause' : 'Resume'}
                </button>
                <div className="flex flex-wrap gap-2">
                  {confirmRevoke ? (
                    <>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => setConfirmRevoke(false)}
                        className="rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={revoke}
                        className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        Revoke key
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => setConfirmRevoke(true)}
                      className="rounded-lg px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
                    >
                      Revoke
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={saving}
                    onClick={saveOrigins}
                    className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-800 disabled:opacity-50 dark:bg-white dark:text-gray-900"
                  >
                    Save sites
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              {originsField}
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  disabled={saving}
                  onClick={publish}
                  className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  Publish
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PublishChatModal;
